import { caseApiRequest } from './apiClient';

export type ConnectedAgdFileRef = {
  connectedAgdFileRefNumber: string;
  /** Omitted when the API returns a ref without a resolvable linked case. */
  caseRequestId?: string;
};

type ConnectedAgdFileRefApiRow = {
  connectedAgdFileRefNumber?: string;
  connected_agd_file_ref_number?: string;
  caseRequestId?: string;
  case_request_id?: string;
};

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

function adaptConnectedAgdFileRef(row: unknown): ConnectedAgdFileRef | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as ConnectedAgdFileRefApiRow;
  const connectedAgdFileRefNumber = str(o.connectedAgdFileRefNumber ?? o.connected_agd_file_ref_number);
  if (!connectedAgdFileRefNumber) return null;
  const caseRequestId = str(o.caseRequestId ?? o.case_request_id);
  return caseRequestId
    ? { connectedAgdFileRefNumber, caseRequestId }
    : { connectedAgdFileRefNumber };
}

/** Task list API row flag (`connected_agd_file_ref_available`). */
export function pickConnectedAgdFileRefAvailable(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const v = (raw as Record<string, unknown>).connected_agd_file_ref_available;
  return v === true;
}

export function taskCaseDetailsPath(caseRequestId: string): string {
  return `/tasks/${encodeURIComponent(caseRequestId.trim())}`;
}

export function parseConnectedAgdFileRefList(value: string): string[] {
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

export function findLinkedCaseRequestId(items: ConnectedAgdFileRef[], fileRef: string): string | null {
  const needle = fileRef.trim().toLowerCase();
  if (!needle) return null;
  const match = items.find((item) => item.connectedAgdFileRefNumber.trim().toLowerCase() === needle);
  return match?.caseRequestId ?? null;
}

/** GET /v1/case-requests/case-requests/{caseRequestId}/connected-agd-file-refs */
export async function fetchConnectedAgdFileRefs(
  caseRequestId: string,
  signal?: AbortSignal,
): Promise<{ ok: true; items: ConnectedAgdFileRef[] } | { ok: false; message: string }> {
  const id = caseRequestId.trim();
  if (!id) return { ok: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(
    `/case-requests/${encodeURIComponent(id)}/connected-agd-file-refs`,
    { method: 'GET', signal },
  );

  if (!res.ok) return { ok: false, message: res.message };

  const raw = Array.isArray(res.data) ? res.data : [];
  const items = raw.map(adaptConnectedAgdFileRef).filter((row): row is ConnectedAgdFileRef => row != null);

  return { ok: true, items };
}
